import { ExpandablePanel } from '@dealstack/frontend/shared/components/ExpandablePanel/ExpandablePanel';
import { NumberInput } from '@dealstack/frontend/shared/components/NumberInput/NumberInput';
import { IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { Controller } from 'react-hook-form';

import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	CreateVestingTermGradedDto,
	UpdateVestingTermGradedDto,
	VestingTermGradedRes,
} from '@dealstack/shared/lib/generated';
import { useGradedTermController } from '@dealstack/frontend/shared/components/VestingTerms/GradedTerm/useGradedTermController';
import { generateNonUniformVestingMessage } from '@dealstack/frontend/shared/components/VestingTerms/helpers/vesting-messages';

export const GradedTerm = ({
	vestingTermId,
	setGradedTermCreatorOpen,
	onCreateVestingTermGraded,
	onUpdateVestingTermGraded,
	onDeleteVestingTermGraded,
	gradedTermCreatorOpen,
	remainingPercentage,
	vestingTermGraded,
}: {
	vestingTermId: string;
	setGradedTermCreatorOpen: (bool: boolean) => void;
	onCreateVestingTermGraded: (vestingTermGraded: CreateVestingTermGradedDto) => void;
	onUpdateVestingTermGraded: (
		gradedTermId: string,
		formValues: UpdateVestingTermGradedDto
	) => void;
	onDeleteVestingTermGraded: (gradedTermId: string) => void;
	gradedTermCreatorOpen?: boolean;
	remainingPercentage: number;
	vestingTermGraded?: VestingTermGradedRes;
}) => {
	const tCommonUi = useTranslationCommonUi();

	const { toggleExpanded, handleSubmit, control, isExpanded, onSubmit, errors } =
		useGradedTermController(
			vestingTermId,
			setGradedTermCreatorOpen,
			onCreateVestingTermGraded,
			onUpdateVestingTermGraded,
			remainingPercentage,
			vestingTermGraded
		);

	const cardTitle = generateNonUniformVestingMessage(tCommonUi, vestingTermGraded);

	return (
		<ExpandablePanel
			title={cardTitle}
			isExpanded={gradedTermCreatorOpen || isExpanded}
			onExpandClick={() => toggleExpanded()}
			cardActions={
				vestingTermGraded
					? [
							{
								title: tCommonUi('vesting_terms.make_changes'),
								onClick: toggleExpanded,
								intent: 'normal',
							},
							{
								title: tCommonUi('vesting_terms.remove'),
								onClick: () => {
									onDeleteVestingTermGraded(vestingTermGraded?.id);
								},
								intent: 'danger',
							},
						]
					: []
			}
		>
			<form className="p-4" onSubmit={handleSubmit(onSubmit)}>
				<div className="flex justify-start w-full">
					<div className="w-1/2 mr-4">
						<div className="font-semibold">
							{tCommonUi('vesting_terms.percentage_label')}:
						</div>

						<Controller
							name="percentage"
							control={control}
							render={({ field }) => (
								<NumberInput
									inputPlaceholder=""
									{...field}
									displayUnit="%"
									errorMessage={
										errors.percentage?.message &&
										errors.percentage.message.toString()
									}
								/>
							)}
						/>
					</div>

					<div className="w-1/2">
						<div className="font-semibold">
							{tCommonUi('vesting_terms.duration_label')}:
						</div>

						<Controller
							name="duration"
							control={control}
							render={({ field }) => (
								<NumberInput
									inputPlaceholder=""
									{...field}
									getDisplayUnit={(value) => {
										return value === 1
											? ` ${tCommonUi('vesting_terms.month')}`
											: ` ${tCommonUi('vesting_terms.months')}`;
									}}
									errorMessage={
										errors.duration?.message &&
										errors.duration.message.toString()
									}
								/>
							)}
						/>
					</div>
				</div>

				<div className="flex justify-between mt-4">
					<button
						className="bg-white border-0 text-red-500 hover:text-red-700 flex items-center justify-end font-bold text-sm"
						onClick={() =>
							gradedTermCreatorOpen
								? setGradedTermCreatorOpen(false)
								: toggleExpanded()
						}
					>
						<IconX stroke={2} size={24} className="mr-1" />
						{tCommonUi('vesting_terms.cancel')}
					</button>

					<button
						className="bg-blue-600 hover:bg-blue-700 flex items-center text-white px-2 py-2 font-semibold rounded border-none"
						type="submit"
					>
						<IconDeviceFloppy stroke={2} size={24} className="mr-1" />
						{tCommonUi('vesting_terms.save')}
					</button>
				</div>
			</form>
		</ExpandablePanel>
	);
};
